// /parlamento/:chamber/:sessionType/:number, the page of a sitting,
// connected: the sitting and its documents (GET /parliament/sittings/…),
// asked again every minute while it is open today and every five minutes
// otherwise, in the foreground only; for the plenary the reduced index of
// the sitting too (GET …/sections, keyed with the sitting's `updated_at`).
// The page is the list of the sitting's documents, every card closed
// (sittingDocuments, committeeDocuments): the type of the document (the meeting,
// the organ), «Aggiornato alle…», its official title, how many entries it has,
// no chip, the bookmark. A document with entries has the chevron down and
// opens in place: «Apri il documento completo», then its entries as links to
// the document at that point (L3, `?voce=`); one without goes to the document
// with the chevron to the right. One list «Documenti»; in a bulletin the
// cards stand in the order served (the official one), the organ as the title.
// Parameters that are
// not a chamber, a type and a number are a 404 without a call. «Torna alle
// sedute» leads to the view of Ultime news the sitting was opened from
// (backToSittings; «Torna alle news» when that view lists no sittings,
// backToSittingsLabel). Every element is one the design already has:
//   frame             the white panel of the pages in development, InDevelopment.tsx:60-61
//   «Torna alle sedute» the way back of the page of a news: an action button without its icon (actions.ts)
//   title, line       the title and the metadata line of the page of a news, NewsReader.tsx:259-263
//   lists, cards      NewsSection + NewsCard, as the lists (LatestNews.tsx); NewsCardOptions: no chip,
//                     the opened body from here, the chevron's names
//   «Apri il documento completo»  the look of «apri la news in pagina» of the opened card (ExpandedNewsCard.tsx)
//   entries           the rows of a section of the page (ROW_CLASS), pl-7 per level as the index's
//                     (SectionIndex.tsx), each a text link, hover in the accent colour; a heading row
//                     (the Camera's seats) is the same row in text, medium weight, no link
//   loading, notices  NewsCardPlaceholder, PanelStatus
import { Button } from "@heroui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router";

import { isApiError } from "../api/client";
import { PanelStatus, STATUS_BUTTON_CLASS } from "../components/PanelStatus";
import { ACTION_LINK_CLASS } from "../components/actions";
import { NewsCardPlaceholder } from "../components/news/NewsCardPlaceholder";
import { NewsSection } from "../components/news/NewsSection";
import type { NewsCardOptions } from "../components/news/NewsCard";
import type { NewsItem } from "../data/mock";
import { toSourceTree } from "../features/news/adapt";
import { useSources } from "../features/news/api";
import { useBookmarks } from "../features/news/bookmarks";
import {
  backToSittings,
  backToSittingsLabel,
  convocationNotice,
  parseSittingKey,
  pollingInterval,
  committeeDocuments,
  sittingDocuments,
  updatedAtLabel,
  type DocumentCard,
  sittingHeading,
  type Chamber,
  type SessionType,
} from "../features/parliament/adapt";
import { useSitting, useSittingIndex } from "../features/parliament/api";
import { romeDay } from "../lib/dates";
import { pageLinkState } from "../lib/navigation";
import { SITE_TITLE } from "../config/site";

export const LOADING_SITTING = "Caricamento della seduta…";
export const SITTING_FAILED = "Seduta non disponibile.";
export const SITTING_NOT_FOUND = "Seduta non trovata.";
export const DOCUMENTS_TITLE = "Documenti";
export const NO_DOCUMENTS = "Nessun documento per questa seduta.";
export const SITTING_REFRESH_FAILED = "Aggiornamento della seduta non riuscito.";
export const SITTING_INDEX_FAILED = "Indice della seduta non disponibile.";
export const OPEN_FULL_DOCUMENT = "Apri il documento completo";
const NOT_FOUND_TITLE = "Seduta non trovata";
const TITLE_ID = "sitting-title";

function useDocumentTitle(title: string | null) {
  useEffect(() => {
    if (title === null) return;
    const previous = document.title;
    document.title = `${title} · ${SITE_TITLE}`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** The white panel with «Torna alle sedute» first; used by every state of the page. */
function Frame({ labelledBy, label, children }: { labelledBy?: string; label?: string; children: React.ReactNode }) {
  const location = useLocation();
  const backTo = backToSittings(location.state);
  return (
    <main
      aria-labelledby={labelledBy}
      aria-label={label}
      className="mt-2.5 min-w-0 flex-1 bg-white px-[19px] pt-2 pb-12"
    >
      <div className="flex flex-col gap-[49px]">
        <div className="flex flex-col gap-[25px]">
          <div className="flex items-center justify-between gap-2.5">
            <Link to={backTo} className={ACTION_LINK_CLASS}>
              {backToSittingsLabel(backTo)}
            </Link>
          </div>
          {children}
        </div>
      </div>
    </main>
  );
}

export function SittingPage() {
  const { chamber = "", sessionType = "", number = "" } = useParams();
  const key = parseSittingKey(chamber, sessionType, number);
  if (!key) return <NotFound />;
  return <ConnectedSittingPage key={`${key.chamber}/${key.sessionType}/${key.number}`} {...key} />;
}

function NotFound() {
  useDocumentTitle(NOT_FOUND_TITLE);
  const message = useRef<HTMLDivElement>(null);
  useEffect(() => message.current?.focus(), []);
  return (
    <Frame label={NOT_FOUND_TITLE}>
      <div ref={message} tabIndex={-1} className="outline-none">
        <PanelStatus role="alert" indent={false}>
          {SITTING_NOT_FOUND}
        </PanelStatus>
      </div>
    </Frame>
  );
}

/** How many top-level entries an opened card shows before «Mostra tutti i N punti». */
export const FIRST_ENTRIES = 12;

/** One level of the entries is a step of 1rem under the body's text. */
const LEVEL_INDENT_REM = 1;

/** The entries of an opened card: the first FIRST_ENTRIES of the top level with their children, then the rest on request. */
function visibleEntries(entries: readonly DocumentEntry[], all: boolean): readonly DocumentEntry[] {
  if (all) return entries;
  const tops = entries.flatMap((entry, i) => (entry.depth === 0 ? [i] : []));
  const cut = tops[FIRST_ENTRIES];
  return cut === undefined ? entries : entries.slice(0, cut);
}

type DocumentEntry = DocumentCard["entries"][number];

/**
 * What the opened card of a document holds: its entries as the text of the
 * opened card (text-xs; the top level medium and dark, the levels under it
 * muted and indented), each a link over the whole row to the document at that
 * point; a heading row (the Camera's seats) is the top-level look without a link.
 */
function DocumentContent({ card }: { card: DocumentCard }) {
  const [all, setAll] = useState(false);
  // the document's page comes back here with «Torna alla seduta», and the sitting keeps its own way back
  const location = useLocation();
  const state = pageLinkState(location);
  const shown = visibleEntries(card.entries, all);
  return (
    <div className="flex flex-col gap-2">
      <ol aria-label={`Punti: ${card.item.title}`} className="flex flex-col gap-1 text-xs leading-5 wrap-anywhere">
        {shown.map((entry) => {
          const look = entry.depth === 0 || entry.heading ? "font-medium text-foreground" : "text-muted";
          return (
            <li key={entry.key} style={{ paddingLeft: `${entry.depth * LEVEL_INDENT_REM}rem` }}>
              {entry.heading ? (
                <span className={look}>{entry.label}</span>
              ) : (
                <Link
                  to={entry.href}
                  state={state}
                  className={`block cursor-pointer rounded-sm hover:text-accent focus-visible:outline-2 focus-visible:outline-accent ${look}`}
                >
                  {entry.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
      {shown.length < card.entries.length && (
        <div className="flex items-center justify-end">
          <Button size="sm" onPress={() => setAll(true)} className={STATUS_BUTTON_CLASS}>
            Mostra tutti i {card.entries.length} punti
          </Button>
        </div>
      )}
    </div>
  );
}

/** The row under the opened card, where NewsActions stands in a news: the one action, to the whole document. */
function DocumentActions({ card }: { card: DocumentCard }) {
  const location = useLocation();
  return (
    <div className="flex items-center justify-end gap-[15px]">
      <Link to={card.item.pageUrl} state={pageLinkState(location)} className={ACTION_LINK_CLASS}>
        {OPEN_FULL_DOCUMENT}
      </Link>
    </div>
  );
}

/** The options of the cards of a sitting's page: no chip, the entries as the opened body, the chevron named for the document. */
function documentOptions(cards: readonly DocumentCard[]): (news: NewsItem) => NewsCardOptions {
  const byId = new Map(cards.map((card) => [card.item.id, card] as const));
  return (news) => {
    const card = byId.get(news.id);
    return {
      showSource: false,
      expandLabel: "Espandi documento",
      collapseLabel: "Comprimi documento",
      ...(card && card.entries.length > 0
        ? { content: <DocumentContent card={card} />, actions: <DocumentActions card={card} /> }
        : {}),
    };
  };
}

function ConnectedSittingPage(key: { chamber: Chamber; sessionType: SessionType; number: number }) {
  const today = romeDay();
  const query = useSitting(key, (detail) => (detail ? pollingInterval([detail.sitting], today) : false));
  // the blocks of both kinds of sitting need the index of the sitting
  const plenary = key.sessionType === "plenaria";
  const index = useSittingIndex(key, query.data?.sitting.updated_at ?? null, query.data !== undefined);
  const catalogue = useSources();
  const bookmarks = useBookmarks();
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);
  // the documents of the sitting, with the entries once the index is there: the plenary's in the order of its state,
  // a bulletin's in the order served
  const documents = useMemo(() => {
    if (!query.data) return [];
    const entries = index.data?.entries ?? null;
    return plenary
      ? sittingDocuments(query.data, entries, tree, search)
      : committeeDocuments(query.data, entries, tree, search);
  }, [plenary, query.data, index.data, tree, search]);
  const options = useMemo(() => documentOptions(documents), [documents]);
  const heading = useMemo(() => (query.data ? sittingHeading(query.data.sitting, today) : null), [query.data, today]);
  const notice = query.data ? convocationNotice(query.data.sitting, today) : null;
  const notFound = query.isError && isApiError(query.error) && query.error.status === 404;
  useDocumentTitle(notFound ? NOT_FOUND_TITLE : (heading?.title ?? null));
  const title = useRef<HTMLHeadingElement>(null);
  // the page opens once in its final shape: the page waits for its index too (a failed index shows the cards)
  const ready = query.data !== undefined && (!index.isPending || index.isError);
  useEffect(() => {
    if (ready) title.current?.focus();
  }, [ready]);

  if (notFound) return <NotFound />;
  if (query.isError && !query.isFetching && !query.data) {
    return (
      <Frame label={SITTING_FAILED}>
        <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
          {SITTING_FAILED}
        </PanelStatus>
      </Frame>
    );
  }
  if (!query.data || !heading || !ready) {
    return (
      <Frame label={LOADING_SITTING}>
        <div role="status" aria-label={LOADING_SITTING} className="flex flex-col gap-[15px]">
          <NewsCardPlaceholder />
          <NewsCardPlaceholder />
        </div>
      </Frame>
    );
  }

  const indexFailed = index.isError && !index.isFetching;
  return (
    <Frame labelledBy={TITLE_ID}>
      <div className="flex flex-col gap-2">
        <h1 id={TITLE_ID} ref={title} tabIndex={-1} className="text-xl leading-7 font-semibold text-brand outline-none">
          {heading.title}
        </h1>
        <p className="text-xs leading-5 text-muted">{heading.line}</p>
      </div>
      {bookmarks.failure && (
        <PanelStatus role="alert" indent={false} onRetry={bookmarks.failure.onRetry}>
          {bookmarks.failure.message}
        </PanelStatus>
      )}
      {query.isError && !query.isFetching && (
        // a poll failed while the documents of the last answer stay on the page, as in the lists of sittings
        <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
          {SITTING_REFRESH_FAILED}
        </PanelStatus>
      )}
      {indexFailed && (
        // the cards stand without their entries
        <PanelStatus role="alert" indent={false} onRetry={() => void index.refetch()}>
          {SITTING_INDEX_FAILED}
        </PanelStatus>
      )}
      {notice && (
        <PanelStatus role="note" indent={false}>
          {notice}
        </PanelStatus>
      )}
      <NewsSection
        title={DOCUMENTS_TITLE}
        dateLabel={updatedAtLabel([query.data.sitting]) ?? undefined}
        items={documents.map((card) => card.item)}
        onToggleSaved={bookmarks.toggle}
        cardOptions={options}
      >
        {documents.length === 0 && <PanelStatus indent={false}>{NO_DOCUMENTS}</PanelStatus>}
      </NewsSection>
    </Frame>
  );
}
